import {createSlice, createAsyncThunk} from '@reduxjs/toolkit';
import jwtAxios from '@crema/services/auth/jwt-auth/jwt-api';

const duration = [
  {
    title: 'Days',
    id: 1,
  },
  {
    title: 'Weeks',
    id: 2,
  },
  {
    title: 'Months',
    id: 3,
  },
];

const additionalDosageFrequencyOptions = [
  {
    dosagevalue : "1",
    freqUnit : "DAILY",
    freqUnitId : 1,
    frequency : "SOS",
    id : "8"
  },
  {
    dosagevalue : "1",
    freqUnit : "DAILY",
    freqUnitId : 1,
    frequency : "STAT",
    id : "9"
  },
];

const initialState = {
  formsList: [],
  formsData: {},
  loading: false,
  error: null,
  clientId: null,
  erxId: null,
  formDirty: {},
  showClientForms: false,
  currentTab: null,
  options: {}, // This will store all the form field options

  clientFormToasters: {
    snackbaropen: false,
    variant: 'success',
    msgdata: '',
  },
};

const clientFormsSlice = createSlice({
  name: 'clientForms',
  initialState,
  reducers: {
    // Simplified options reducer action
    setOptions(state, action) {
      const {fieldName, options} = action.payload;
      state.options[fieldName] = options;
    },
    setFormsList(state, action) {
      state.formsList = action.payload;
    },
    setFormsData(state, action) {
      state.formsData = action.payload;
    },
    setShowClientForms(state, action) {
      state.showClientForms = action.payload;
    },
    setClientInfo(state, action) {
      state.clientId = action.payload.clientId;
      state.erxId = action.payload.erxId;
      state.showClientForms = action.payload.showClientForms;
    },
    setSnackBarValues(state, action) {
      state.clientFormToasters.snackbaropen = action.payload.snackbaropen;
      state.clientFormToasters.variant = action.payload.variant;
      state.clientFormToasters.msgdata = action.payload.msgdata;
    },
    setSnackBarToDefaultValues(state, action) {
      state.clientFormToasters.snackbaropen = false;
      state.clientFormToasters.variant = 'success';
      state.clientFormToasters.msgdata = '';
    },
    setCurrentTab(state, action) {
      state.currentTab = action.payload;
    },
    // setFormDirty(state, action) {
    //   const {formId, isDirty} = action.payload;
    //   state.formDirty[formId] = Boolean(isDirty);
    // },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClientForms.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClientForms.fulfilled, (state, action) => {
        state.loading = false;
        state.formsList = action.payload || [];
      })
      .addCase(fetchClientForms.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export const {
  setFormsList,
  setFormsData,
  setShowClientForms,
  setClientInfo,
  setOptions,
  setSnackBarValues,
  setSnackBarToDefaultValues,
  setCurrentTab,
} = clientFormsSlice.actions;

export const selectFormsList = (state) => state.clientForms.formsList;
export const selectFormsData = (state) => state.clientForms.formsData;
export const selectFormsLoadingState = (state) => state.clientForms.loading;
export const selectFormsErrorState = (state) => state.clientForms.error;
export const selectShowClientForms = (state) =>
  state.clientForms.showClientForms;
export const selectClientID = (state) => state.clientForms.clientId;
export const selectERXID = (state) => state.clientForms.erxId;
// export const selectFormDirty = (state) => state.clientForms.formDirty;

export const selectCurrentTab = (state) => state.clientForms.currentTab;

export const selectOptionsByField = (fieldName) => (state) =>
  state.clientForms?.options?.[fieldName] || [];

export const selectAllOptionsRequired = (state) => {
  return {
    route: state.dashboard?.routeData || [],
    unit: state.dashboard?.sarvoUnits || [],
    dosageFrequency: [
      ...additionalDosageFrequencyOptions,
      ...state.dashboard?.sarvoDosageOption || [],
    ],
    duration: state.dashboard?.durationList || [],
  };
};

export const selectSnackBarValues = (state) => ({
  clientFormToasters: state.clientForms.clientFormToasters,
});

export const fetchClientForms = createAsyncThunk(
  'clientForms/fetchClientForms',
  async ({userCode, clientId}, {rejectWithValue}) => {
    try {
      if (!userCode || !clientId) {
        throw new Error('User code and client ID are required');
      }

      // First, get the JWT token
      const tokenResponse = await jwtAxios.post(
        'v1/master/getClientFormsListJWT',
        {
          userCode,
          clientId: parseInt(clientId),
        },
      );

      const token = tokenResponse.data?.body?.token;

      if (!token) {
        throw new Error('Failed to get authentication token');
      }

      // Then fetch the forms using the token
      const formsResponse = await jwtAxios.post(
        'v1/master/getClientFormsList',
        {
          jwtToken: token,
        },
      );

      return formsResponse?.data?.body || [];
    } catch (error) {
      console.error('Error in fetchClientForms:', error);
      return rejectWithValue(error.message || 'Failed to fetch client forms');
    }
  },
);

export default clientFormsSlice.reducer;
